import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Data } from '@angular/router';
import {
    AfterViewInit,
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    DestroyRef, OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    BehaviorSubject,
    concatMap,
    filter,
    first,
    firstValueFrom,
    map,
    mergeMap,
    Observable,
    take,
    tap,
} from 'rxjs';
import { Sale } from '../../../shared/interfaces/sale';
import { Workflow } from '../../../shared/interfaces/workflow';
import { SalesService } from '../../../shared/services/sales.service';
import { Cart } from '../../classes/cart';
import { StepperComponent } from '../../components/stepper/stepper.component';
import { SelectionChange } from '../../interfaces/selection-change';
import { CustomerStepFormsService } from '../../services/customer-step-forms.service';
import { PurlParamsService } from '../../services/purl-params.service';
import { Product } from '../../../shared/interfaces/product';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-sale-workflow',
    templateUrl: './sale-workflow.component.html',
    styleUrl: './sale-workflow.component.scss',
})
export class SaleWorkflowComponent implements AfterViewInit, OnInit, OnDestroy {
    public form: FormArray | undefined;

    public queryStep$: Observable<number> = this.route.queryParamMap.pipe(
        first(),
        map((query) => query.get('step')),
        filter((query) => !!query),
        map((step) => parseInt(step!, 10)),
    );

    public sale$: Observable<Sale> = this.route.data.pipe(
        map((d) => d['sale'] as Sale),
        filter((v) => !!v),
    );

    public slideOut: boolean | undefined;

    public stepBackward$: Observable<SelectionChange> | undefined;

    public stepChange$: Observable<SelectionChange> | undefined;

    public stepForward$: Observable<SelectionChange> | undefined;

    @ViewChild(StepperComponent) public stepper: StepperComponent | undefined;

    public readonly workflowForm$: Observable<Workflow>;

    public constructor(
        public readonly cart: Cart,
        private readonly route: ActivatedRoute,
        private readonly forms: CustomerStepFormsService,
        private readonly destroyRef: DestroyRef,
        private readonly sales: SalesService,
        private readonly cdr: ChangeDetectorRef,
        private readonly purlParams: PurlParamsService,
    ) {
        this.purlParams.parseFromQueryParams(this.route.snapshot.queryParamMap);
        this.workflowForm$ = this.sale$.pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((s) => this.form = this.forms.fromWorkflow(s, s.workflow)),
            map((s) => s.workflow),
        );
    }

    public next(): void {
        this.validate() && this.stepper?.next();
    }

    public ngAfterViewInit(): void {
        this.stepChange$ = this.stepper?.selectionChange.pipe(
            takeUntilDestroyed(this.destroyRef),
        );

        this.stepForward$ = this.stepChange$?.pipe(
            filter((e) => e.selectedIndex > e.previouslySelectedIndex),
        );

        this.stepBackward$ = this.stepChange$?.pipe(
            filter((e) => e.selectedIndex < e.previouslySelectedIndex),
        );

        this.handleStepSelectionChange();
    }

    public ngOnDestroy(): void {
        this.cart.clear();
    }

    public ngOnInit(): void {
        this.initialize();
    }

    public refresh(): void {
        const encoded = this.route.snapshot.paramMap.get('encoded');
        if (!encoded) return;

        this.sales.fromUrl(encoded).pipe(
            take(1),
            tap((sale) => {
                (this.route.data as BehaviorSubject<Data>).next({ sale });
                this.cart.sale = sale;
                this.cdr.markForCheck();
            }),
        ).subscribe();
    }

    private addOemStylesheet(sale: Sale): void {
        if (!sale.store?.oem) {
            console.warn('Store must set an oem in settings to properly style the sale section');
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://oem.dv-3.com/variables?make=' + sale.store?.oem;
        document.head.appendChild(link);
    }

    private handleStepSelectionChange(): void {
        this.stepBackward$?.pipe(
            takeUntilDestroyed(this.destroyRef),
            mergeMap(() => this.sale$.pipe(first())),
            mergeMap((sale: Sale) => this.forms.getCartProducts(sale)),
            tap((products: Product[]) => {
                this.cart.clear();
                products.forEach((p: Product) => this.cart.toggle(p, { markDirty: false }));
            }),
            tap(() => this.cart.markAsClean()),
        ).subscribe(() => {
            this.cdr.markForCheck();
        });

        this.stepForward$?.pipe(
            takeUntilDestroyed(this.destroyRef),
            mergeMap((e) => this.sale$.pipe(first(), map((sale) => ({ sale, event: e })))),
            mergeMap((data) => {
                if (this.cart.dirty) {
                    return this.sales.saveCart(data.sale.workflow.id, data.sale.id, this.cart).pipe(
                        tap((sale: Sale) => {
                            this.cart.clear();
                            sale.products.forEach((p: Product) => this.cart.toggle(p, { markDirty: false }));
                        }),
                        tap(() => this.cart.markAsClean()),
                        map(() => data),
                    );
                }
                return [data];
            }),
            map((data) => ({ ...data, form: this.form?.at(data.event.previouslySelectedIndex)?.value })),
            mergeMap((data) => {
                const prev = data.event.previouslySelectedIndex;
                return this.sales.progress(
                    data.sale.workflow.id,
                    data.sale.id,
                    {
                        form: data.form,
                        previous_index: prev,
                        previous_slug: data.sale.workflow.steps?.at(prev)?.slug,
                        step_index: data.event.selectedIndex,
                    },
                );
            }),
        ).subscribe(async (response) => {
            // In the event that products or customer info were edited, this will sync them up without having to re-request
            const sale = (await firstValueFrom(this.sale$));
            sale.coupons = response.coupons;
            sale.prizes = response.prizes;
            sale.products = response.products;
            sale.customers = response.customers;
            sale.addresses = response.addresses;
            sale.employment_histories = response.employment_histories;
            sale.sale_properties = response.sale_properties;
            sale.make_id = response.make_id;
            sale.model_id = response.model_id;
            sale.trim_id = response.trim_id;
            sale.orderSummary = response.orderSummary;
            sale.make = response.make;
            sale.model = response.model;
            sale.trim = response.trim;

            (this.route.data as BehaviorSubject<Data>).next({ sale: { ...sale } });

            this.cdr.markForCheck();
        });
    }

    private initialize(): void {
        this.sale$.pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            tap((sale) => this.addOemStylesheet(sale)),
            tap((sale) => this.cart.sale = sale),
            concatMap(sale => this.forms.getCartProducts(sale)),
            tap(products => products.forEach(p => this.cart.toggle(
                p, { markDirty: false }),
            )),
        ).subscribe();
    }

    private updateFormValidity(form: AbstractControl): void {
        if (form instanceof FormControl) {
            form.markAsTouched();
            form.markAsDirty();
            form.updateValueAndValidity({ emitEvent: true });
        } else if (form instanceof FormGroup) {
            Object.values(form.controls).forEach(control => this.updateFormValidity(control));
        } else if (form instanceof FormArray) {
            form.controls.forEach(control => this.updateFormValidity(control));
        }
    }

    private validate(): boolean {
        if (!this.form || !this.stepper) {
            return false;
        }

        const isValid = this.form.at(this.stepper.currentStep).valid;
        if (!isValid) {
            this.updateFormValidity(this.form.at(this.stepper.currentStep!));
        }

        return isValid;
    }
}
